import { z } from 'zod';

import { ApiRequestError } from './api';

const phoneNumberSchema = z
  .string()
  .trim()
  .regex(/^\d{7,15}$/, 'Enter a valid phone number.');

const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters.')
  .max(64, 'Username must be 64 characters or less.');

const passwordSchema = z
  .string()
  .trim()
  .min(4, 'Password must be at least 4 characters.')
  .max(128, 'Password must be 128 characters or less.');

const serviceNameSchema = z
  .string()
  .trim()
  .min(2, 'Service name must be at least 2 characters.')
  .max(120, 'Service name must be 120 characters or less.');

const staffNameSchema = z
  .string()
  .trim()
  .min(2, 'Staff name must be at least 2 characters.')
  .max(120, 'Staff name must be 120 characters or less.');

const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required.')
  .email('Enter a valid email address.');

const positiveWholeNumberSchema = (requiredMessage: string, invalidMessage: string) =>
  z
    .string()
    .trim()
    .min(1, requiredMessage)
    .regex(/^\d+$/, invalidMessage)
    .transform((value) => Number(value))
    .refine((value) => Number.isInteger(value) && value >= 0, invalidMessage);

const positiveAmountSchema = z
  .string()
  .trim()
  .min(1, 'Enter the total amount.')
  .regex(/^\d+$/, 'Enter a valid total amount.')
  .transform((value) => Number(value))
  .refine((value) => Number.isInteger(value) && value > 0, 'Enter a valid total amount.');

export const loginUsernamePasswordSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
});

export const loginPhoneSchema = z.object({
  phoneNumber: phoneNumberSchema,
});

export const loginOtpSchema = z.object({
  otp: z.string().trim().regex(/^\d{6}$/, 'Enter the 6 digit OTP.'),
});

export const passwordChangeSchema = z
  .object({
    currentPassword: passwordSchema,
    newPassword: passwordSchema,
    confirmPassword: passwordSchema,
  })
  .superRefine((value, context) => {
    if (value.newPassword !== value.confirmPassword) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: 'New password and confirmation do not match.',
      });
    }
  });

export const serviceFormSchema = z.object({
  name: serviceNameSchema,
  reminderDays: positiveWholeNumberSchema(
    'Reminder days are required.',
    'Enter a valid reminder day count.'
  ),
  amount: positiveWholeNumberSchema('Amount is required.', 'Enter a valid amount.'),
});

export const createStaffFormSchema = z.object({
  name: staffNameSchema,
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  password: passwordSchema,
});

export const updateStaffFormSchema = z.object({
  name: staffNameSchema,
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  password: z.union([passwordSchema, z.literal('')]),
});

export const visitorFormSchema = z.object({
  visitorName: z
    .string()
    .trim()
    .min(2, 'Visitor name must be at least 2 characters.')
    .max(120, 'Visitor name must be 120 characters or less.'),
  visitorPhone: phoneNumberSchema,
  visitFrequency: z.string().trim().max(60, 'Visit frequency must be 60 characters or less.'),
  amountText: positiveAmountSchema,
  services: z.array(z.string().trim().min(1)).min(1, 'Select at least one service.'),
});

type FieldErrors = Record<string, string[] | undefined>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function firstMessage(messages: string[] | undefined): string | null {
  if (!messages) {
    return null;
  }

  const message = messages.find((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()));
  return message ?? null;
}

export function flattenFieldErrors(fieldErrors: FieldErrors) {
  const nextFieldErrors: Record<string, string> = {};

  Object.entries(fieldErrors).forEach(([field, messages]) => {
    const message = firstMessage(messages);

    if (message) {
      nextFieldErrors[field] = message;
    }
  });

  return nextFieldErrors;
}

function extractFieldErrors(details: unknown) {
  if (!isRecord(details)) {
    return {};
  }

  const issues = details.issues;

  if (!isRecord(issues)) {
    return {};
  }

  const fieldErrors = issues.fieldErrors;

  if (!isRecord(fieldErrors)) {
    return {};
  }

  return flattenFieldErrors(fieldErrors as FieldErrors);
}

export function getFieldErrorsFromApiError(error: unknown) {
  if (!(error instanceof ApiRequestError)) {
    return {};
  }

  return extractFieldErrors(error.details);
}
